import {
    Button,
    ButtonGroup,
    Form,
    Labelled,
    Stack,
    TextField,
} from "@shopify/polaris";
import React, { useCallback, useEffect, useReducer, useState } from "react";
import { arrInvalid, reducerFn } from "../../helper";
import { ComponentLabelPolaris } from "../shared/ComponentLabelPolaris";
import history from "../../history";
import { sortFields } from "../amazon-idea/helper";
import AmazonIdeaFields from "../amazon-idea/amazon-idea-field";

function AmazonIdeaTemplateForm({ loading, onSubmit, value }) {
    const [state, setState] = useReducer(reducerFn, {
        title: "",
        fields: [],
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!value) return;

        const { title, fields } = value || {};

        const newState = {
            title,
            fields: arrInvalid(fields) ? [] : sortFields(fields),
        };

        setState(newState);
    }, [JSON.stringify(value)]);

    const handleStateChange = useCallback((value, id) => {
        setState({ [id]: value });

        const error = validateState(value, id);
        setErrors((p) => ({ ...p, ...error }));
    }, []);

    const handleValidateStates = async () => {
        let errors = {};
        Object.entries(state).forEach(([key, value]) => {
            const err = validateState(value, key);
            if (err) {
                errors = { ...errors, ...err };
            }
        });

        if (Object.values(errors).filter(Boolean).length > 0) {
            setErrors((p) => ({ ...p, ...errors }));
            return true;
        }

        return false;
    };

    const handleSubmit = useCallback(async () => {
        const hasError = await handleValidateStates();
        if (hasError) return;

        const input = prepareInput(state);
        onSubmit(input);
    }, [onSubmit, state, handleValidateStates]);

    return (
        <Form onSubmit={handleSubmit}>
            <Stack vertical>
                <div>
                    <ComponentLabelPolaris required label="Title" />
                    <TextField
                        id="title"
                        placeholder="Enter Title"
                        value={state.title}
                        error={errors?.title || ""}
                        onChange={handleStateChange}
                    />
                </div>

                <div>
                    <Labelled label="Custom Fields" />
                    <AmazonIdeaFields
                        fields={state.fields}
                        setState={setState}
                        defaultOpen={!value}
                    />
                </div>

                <Stack distribution="trailing">
                    <ButtonGroup>
                        <Button
                            children="Cancel"
                            onClick={() => {
                                history.goBack();
                            }}
                        />
                        <Button
                            submit
                            children="Submit"
                            primary
                            loading={loading}
                        />
                    </ButtonGroup>
                </Stack>
            </Stack>
        </Form>
    );
}

const FIELDS_REQUIRED = {
    title: "Idea Template title is required",
};

function validateState(value, id) {
    const res = {};

    if (FIELDS_REQUIRED[id]) {
        res[id] = null;

        if (!value) {
            res[id] = FIELDS_REQUIRED[id];
        } else if (typeof value !== "string" && arrInvalid(value)) {
            res[id] = FIELDS_REQUIRED[id];
        }
    }

    return res;
}

function prepareInput(state) {
    if (!arrInvalid(state.fields)) {
        state.fields = state.fields.map((field, index) => ({
            type: field.type,
            label: field.label,
            instructions: field.instructions,
            sampleText: field.sampleText,
            min: field.min ? Number(field.min) : undefined,
            max: field.max ? Number(field.max) : undefined,
            placeholder: field.placeholder,
            ordering: index,
            optionsList: (field.optionsList || []).map((opt) => ({
                name: opt.name,
                priceDifference: Number(opt?.priceDifference || 0),
            })),
        }));
    }

    const { kpi, ...input } = state;
    return input;
}

export default AmazonIdeaTemplateForm;
